import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatNumber, messages, type ListSummary } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

/**
 * 企業一覧からキャンペーンの対象リストを作る。
 * 選択した企業を固定する静的リストと、いまの絞り込みを保存する条件リストのどちらかを選ぶ。
 * 条件リストの対象はキャンペーン開始時点で評価される（docs/04-data-model.md D08）。
 */
export const CampaignListDialog = ({
  open,
  onOpenChange,
  companyIds,
  filter,
  filteredCount,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 選択中の企業。0 件なら条件リストのみ作れる */
  companyIds: string[];
  /** いまの絞り込み条件（空の値は除いたもの） */
  filter: Record<string, string>;
  /** 絞り込みに一致する総件数（不明なら undefined） */
  filteredCount?: number;
  onDone: () => void;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'static' | 'dynamic'>('static');
  const [name, setName] = useState('');
  const [goToCampaign, setGoToCampaign] = useState(true);

  const hasSelection = companyIds.length > 0;
  const hasFilter = Object.keys(filter).length > 0;

  // 選択が無ければ条件リストしか作れない
  useEffect(() => {
    if (open) setMode(hasSelection ? 'static' : 'dynamic');
  }, [open, hasSelection]);

  const create = useMutation({
    mutationFn: () =>
      api.post<SingleResponse<ListSummary>>('/lists', {
        name: name.trim(),
        type: mode,
        ...(mode === 'static' ? { companyIds } : { filterJson: filter }),
      }),
    onSuccess: (result) => {
      toast.success(messages.list.createdForCampaign(result.data.name, result.data.companyCount));
      void queryClient.invalidateQueries({ queryKey: qk.lists });
      onOpenChange(false);
      setName('');
      onDone();
      if (goToCampaign) navigate(`/campaigns/new?listId=${result.data.id}`);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const targetCount = mode === 'static' ? companyIds.length : filteredCount;

  // 押せない理由は必ず画面に出す。無効なボタンだけを見せると「作成されない」と受け取られるため
  const blockedReason =
    mode === 'static' && !hasSelection
      ? '一覧で企業を選んでから開いてください。絞り込み条件から作ることもできます'
      : mode === 'dynamic' && !hasFilter
        ? '絞り込み条件が指定されていません。一覧で条件を指定するか、企業を選んでください'
        : !name.trim()
          ? 'リスト名を入力してください'
          : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{messages.list.createForCampaign}</DialogTitle>
          <DialogDescription>
            作ったリストはそのままキャンペーンの対象に指定できます。
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-body font-semibold">対象の決め方</legend>
            {(
              [
                {
                  value: 'static',
                  label: `${messages.list.fromSelection}（${formatNumber(companyIds.length)} 社）`,
                  note: messages.list.fromSelectionNote,
                  disabled: !hasSelection,
                },
                {
                  value: 'dynamic',
                  label: messages.list.fromFilter,
                  note: messages.list.fromFilterNote,
                  disabled: !hasFilter,
                },
              ] as const
            ).map((option) => (
              <label
                key={option.value}
                className={`flex min-h-touch items-start gap-2 text-body ${
                  option.disabled ? 'text-foreground-subtle' : ''
                }`}
              >
                <input
                  type="radio"
                  name="campaign-list-mode"
                  className="mt-1 size-4 accent-foreground"
                  checked={mode === option.value}
                  disabled={option.disabled}
                  onChange={() => setMode(option.value)}
                />
                <span className="min-w-0">
                  <span className="block">{option.label}</span>
                  <span className="block text-note text-foreground-muted">
                    {option.disabled
                      ? option.value === 'static'
                        ? '企業を選ぶと使えます'
                        : '絞り込み条件を指定すると使えます'
                      : option.note}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>

          <Field label="リスト名" required>
            {(props) => (
              <Input
                {...props}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="高判定_フォームあり"
              />
            )}
          </Field>

          {targetCount != null && (
            <p className="text-note text-foreground-muted">
              いまの対象は <span className="tabular">{formatNumber(targetCount)}</span> 社です。
              {mode === 'dynamic' && ' 条件リストなので、あとから増減することがあります。'}
            </p>
          )}

          {blockedReason && (
            <p className="flex items-start gap-1.5 text-note font-semibold text-foreground">
              <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
              {blockedReason}
            </p>
          )}

          <label className="flex min-h-touch items-center gap-2 text-body">
            <input
              type="checkbox"
              className="size-4 accent-foreground"
              checked={goToCampaign}
              onChange={(e) => setGoToCampaign(e.target.checked)}
            />
            {messages.list.goToCampaign}
          </label>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            size="lg"
            onClick={() => create.mutate()}
            disabled={create.isPending || blockedReason != null}
          >
            {messages.list.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
